import { TARGETED_CARDS } from './cards';
import {
  areAdjacent,
  connectedThroughOwned,
  currentPlayer,
  frontierOf,
  isPinned,
  ownedBy,
  territoryDef,
} from './helpers';
import type { Action, GameState, MapDef, PlayerId, TerritoryId } from './types';

/**
 * A representative set of legal actions for the player. Not exhaustive for
 * numeric parameters: `place` is offered one unit at a time, `attack` with the
 * maximum dice, `occupy` at both ends of its range, `fortify` with every army
 * that can move. The reducer validates any count the caller chooses instead.
 */
export function legalActions(map: MapDef, state: GameState, playerId: PlayerId): Action[] {
  if (state.outcome) return [];
  if (currentPlayer(state).id !== playerId) return [];
  const me = playerId;
  const out: Action[] = [];

  if (state.pendingOccupy) {
    const p = state.pendingOccupy;
    const max = state.territories[p.from]!.armies - 1;
    out.push({ type: 'occupy', playerId: me, count: p.minArmies });
    if (max > p.minArmies) out.push({ type: 'occupy', playerId: me, count: max });
    return out;
  }

  const owned = ownedBy(state, me).sort();

  if (state.reinforcementsPending > 0 && (state.phase === 'reinforce' || state.phase === 'draw')) {
    for (const id of owned) out.push({ type: 'place', playerId: me, territoryId: id, count: 1 });
  } else if (state.phase === 'draw' && state.drawsPending > 0) {
    out.push({ type: 'drawCard', playerId: me });
  }

  if (state.phase === 'attack') {
    for (const fromId of owned) {
      const from = state.territories[fromId]!;
      if (from.zombies > 0 && from.armies >= 1) {
        out.push({
          type: 'attack',
          playerId: me,
          from: fromId,
          to: fromId,
          dice: Math.min(3, from.armies) as 1 | 2 | 3,
        });
      }
      if (from.armies < 2 || isPinned(state, fromId)) continue;
      const dice = Math.min(3, from.armies - 1) as 1 | 2 | 3;
      for (const toId of territoryDef(map, fromId).neighbors) {
        if (state.territories[toId]!.owner === me) continue;
        out.push({ type: 'attack', playerId: me, from: fromId, to: toId, dice });
      }
    }
    out.push({ type: 'endAttack', playerId: me });
    out.push({ type: 'endTurn', playerId: me });
  }

  if (state.phase === 'fortify') {
    if (!state.fortified) {
      for (const fromId of owned) {
        const from = state.territories[fromId]!;
        if (from.armies < 2) continue;
        for (const toId of owned) {
          if (toId === fromId) continue;
          if (
            !areAdjacent(map, fromId, toId) &&
            !connectedThroughOwned(state, map, me, fromId, toId)
          )
            continue;
          out.push({
            type: 'fortify',
            playerId: me,
            from: fromId,
            to: toId,
            count: from.armies - 1,
          });
        }
      }
    }
    out.push({ type: 'endTurn', playerId: me });
  }

  if (state.phase !== 'ended') {
    const hand = currentPlayer(state).hand;
    const frontier = frontierOf(state, map, me);
    for (const cardId of new Set(hand)) {
      if (!TARGETED_CARDS.has(cardId)) continue;
      const targets: TerritoryId[] =
        cardId === 'airstrike'
          ? [...owned, ...frontier].filter((id) => state.territories[id]!.zombies > 0)
          : cardId === 'cleanup-crew'
            ? owned.filter((id) => state.territories[id]!.zombies > 0)
            : owned;
      for (const target of targets) out.push({ type: 'playCard', playerId: me, cardId, target });
    }
  }

  return out;
}
